import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { generateDeviceLinkPin, generateSalt, derivePinWrapKey, hashPin, wrapChatKey } from "../lib/crypto.js";
import { getSettings, patchSettings, revokeThisDevice, createDeviceLink, createAccountInvite } from "../lib/api.js";
import { clearSession, type Session } from "../lib/session.js";

interface SettingsProps {
  session: Session;
  onRevoked: () => void;
}

interface ActiveLink {
  pin: string;
  linkId: string;
  expiresAt: number;
  qrDataUrl: string | null;
}

/**
 * Pruned per Decision 9: exactly (a) a read-only E2EE status line, (b) the
 * notification-preview toggle, (c) a "Revoke this device" action, plus
 * (d) the Revision 5 device-link PIN flow (Decision 12), which replaces
 * Revision 4's always-shown static `{apiUrl, username}` QR — that QR is now
 * generated on demand alongside a fresh link/PIN, and additionally encodes
 * `linkId` (never the PIN, never the password).
 */
export default function Settings({ session, onRevoked }: SettingsProps) {
  const [e2eeRequired, setE2eeRequired] = useState<boolean | null>(null);
  const [previewOn, setPreviewOn] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [activeLink, setActiveLink] = useState<ActiveLink | null>(null);
  const [linkStatus, setLinkStatus] = useState<"idle" | "working" | "error">("idle");
  const [linkError, setLinkError] = useState<string | null>(null);
  const [remainingMs, setRemainingMs] = useState(0);
  const [inviteStatus, setInviteStatus] = useState<"idle" | "working" | "error">("idle");
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [activeInvite, setActiveInvite] = useState<{ code: string; expiresAt: number } | null>(null);

  useEffect(() => {
    getSettings().then((s) => {
      setE2eeRequired(s.e2eeRequired);
      setPreviewOn(s.notificationPreviewWhenE2eeOff);
    });
  }, []);

  useEffect(() => {
    if (!activeLink) return;
    const tick = () => setRemainingMs(Math.max(0, activeLink.expiresAt - Date.now()));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [activeLink]);

  async function handleToggle(checked: boolean) {
    setPreviewOn(checked);
    await patchSettings(checked);
  }

  async function handleRevoke() {
    if (!window.confirm("Revoke this device? You will need to re-pair to use Bridge here again.")) return;
    setRevoking(true);
    try {
      await revokeThisDevice();
      clearSession();
      onRevoked();
    } finally {
      setRevoking(false);
    }
  }

  async function handleGenerateLink() {
    setLinkStatus("working");
    setLinkError(null);
    try {
      const pin = generateDeviceLinkPin();
      const linkSalt = generateSalt();
      const pinWrapKey = await derivePinWrapKey(pin, linkSalt);
      const wrappedChatKeyForPin = wrapChatKey(session.chatKey, pinWrapKey);
      const pinHash = hashPin(pin, linkSalt);

      const { linkId, expiresAt } = await createDeviceLink({ linkSalt, pinHash, wrappedChatKeyForPin });

      const qrPayload = JSON.stringify({ apiUrl: window.location.origin, username: session.username, linkId });
      const qrDataUrl = await QRCode.toDataURL(qrPayload, { margin: 1, width: 220 }).catch(() => null);

      setActiveLink({ pin, linkId, expiresAt, qrDataUrl });
      setLinkStatus("idle");
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : "device_link_failed");
      setLinkStatus("error");
    }
  }

  async function handleGenerateInvite() {
    setInviteStatus("working");
    setInviteError(null);
    try {
      const { code, expiresAt } = await createAccountInvite();
      setActiveInvite({ code, expiresAt });
      setInviteStatus("idle");
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : "account_invite_failed");
      setInviteStatus("error");
    }
  }

  const remainingLabel =
    activeLink && remainingMs > 0
      ? `${Math.floor(remainingMs / 60000)}:${String(Math.floor((remainingMs % 60000) / 1000)).padStart(2, "0")}`
      : "Expired";

  return (
    <div className="rounded-xl border border-slate-800 bg-gray-900 p-4">
      <h1 className="text-xl font-semibold text-slate-100">Settings</h1>

      <div className="flex items-center justify-between border-b border-slate-800 py-2">
        <span className="text-slate-200">End-to-end encryption</span>
        <strong className="text-blue-300">{e2eeRequired === null ? "…" : e2eeRequired ? "On" : "Off (env-var override)"}</strong>
      </div>
      <p className="mt-2 text-sm text-slate-400">
        Read-only. Change only by editing <code>E2EE_REQUIRED</code> in the server's env file
        (<code>/etc/bridge/bridge.env</code>) and restarting the service — see README.md.
      </p>

      <div className="mt-2 flex items-center justify-between border-b border-slate-800 py-2">
        <span className="text-slate-200">Show message preview in notifications</span>
        <input
          type="checkbox"
          checked={previewOn}
          onChange={(e) => handleToggle(e.target.checked)}
          disabled={e2eeRequired !== false}
        />
      </div>
      <p className="mt-2 text-sm text-slate-400">
        Only takes effect if end-to-end encryption is off — otherwise notifications always show a generic
        "New message" text.
      </p>

      <button
        type="button"
        onClick={handleRevoke}
        disabled={revoking}
        className="mt-4 cursor-pointer rounded-lg bg-red-700 px-4 py-2 text-white disabled:cursor-not-allowed disabled:opacity-60"
      >
        Revoke this device
      </button>
      <p className="mt-2 text-xs text-slate-500">Device: {session.deviceId}</p>

      <hr className="my-6 border-slate-800" />
      <h2 className="text-base font-semibold text-slate-100">Set up another device</h2>
      <p className="mt-2 text-sm text-slate-400">
        Generate a short-lived PIN and enter it (with your username) on the new device's "Activate with
        PIN" screen — no need to type your full password there. The PIN is single-use and expires soon.
      </p>

      {!activeLink && (
        <button
          type="button"
          onClick={handleGenerateLink}
          disabled={linkStatus === "working"}
          className="mt-3 cursor-pointer rounded-lg bg-blue-600 px-4 py-2.5 text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {linkStatus === "working" ? "Generating…" : "Generate device-link PIN"}
        </button>
      )}
      {linkStatus === "error" && linkError && <p className="mt-2 text-sm text-red-400">{linkError}</p>}

      {activeLink && (
        <div className="mt-3 flex flex-col items-center gap-3 rounded-lg border border-slate-700 bg-[#0b1220] p-4">
          <div className="text-3xl font-bold tracking-[0.3em] text-slate-100">{activeLink.pin}</div>
          <div className={`text-sm ${remainingMs > 0 ? "text-slate-400" : "text-red-400"}`}>
            {remainingMs > 0 ? `Expires in ${remainingLabel}` : "This PIN has expired — generate a new one."}
          </div>
          {activeLink.qrDataUrl && (
            <img src={activeLink.qrDataUrl} alt="Bridge device-link QR code" width={220} height={220} />
          )}
          <p className="text-center text-xs text-slate-500">Username: {session.username}</p>
          <button
            type="button"
            onClick={handleGenerateLink}
            disabled={linkStatus === "working"}
            className="cursor-pointer rounded-lg border border-slate-700 bg-transparent px-4 py-2 text-slate-200"
          >
            Generate a new one
          </button>
        </div>
      )}

      <hr className="my-6 border-slate-800" />
      <h2 className="text-base font-semibold text-slate-100">Invite a new account</h2>
      <p className="mt-2 text-sm text-slate-400">
        This deploy supports a limited number of independent accounts (see <code>MAX_ACCOUNTS</code> in the
        server's env file), each with its own fully private chat. Any account can generate a one-time invite code
        and share it (out of band) with the person setting up the next account — they'll enter it on the
        account-creation screen.
      </p>
      {!activeInvite && (
        <button
          type="button"
          onClick={handleGenerateInvite}
          disabled={inviteStatus === "working"}
          className="mt-3 cursor-pointer rounded-lg bg-blue-600 px-4 py-2.5 text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {inviteStatus === "working" ? "Generating…" : "Generate account-invite code"}
        </button>
      )}
      {inviteStatus === "error" && inviteError && <p className="mt-2 text-sm text-red-400">{inviteError}</p>}
      {activeInvite && (
        <div className="mt-3 flex flex-col items-center gap-3 rounded-lg border border-slate-700 bg-[#0b1220] p-4">
          <div className="break-all text-center text-lg font-bold tracking-wider text-slate-100">
            {activeInvite.code}
          </div>
          <p className="text-center text-xs text-slate-500">
            Shown once — copy it now. Expires {new Date(activeInvite.expiresAt).toLocaleString()}.
          </p>
          <button
            type="button"
            onClick={handleGenerateInvite}
            disabled={inviteStatus === "working"}
            className="cursor-pointer rounded-lg border border-slate-700 bg-transparent px-4 py-2 text-slate-200"
          >
            Generate a new one
          </button>
        </div>
      )}
    </div>
  );
}
